import React from 'react';
import { Target, Eye, TrendingUp, Heart, Brain, GraduationCap, DollarSign, Trophy, Handshake } from 'lucide-react';
import { Card } from '../components/ui/card';
import { impactAreas } from '../mockData';

const About = () => {
  const getIcon = (iconName) => {
    const icons = {
      Brain,
      GraduationCap,
      DollarSign,
      Heart,
      Trophy,
      Handshake
    };
    return icons[iconName] || Target;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              About <span className="text-[#E6B933]">WHY ME Foundation</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Wisdom Helping Youth Maximize Everything - A nonprofit committed to expanding opportunities for underprivileged youth through education, mentorship, and structured development.
            </p>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 border-t-4 border-[#E6B933] hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#E6B933]/10 rounded-xl flex items-center justify-center mb-6">
                <Target className="text-[#E6B933]" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To Influence, Inform, and Impact young people by equipping them with the intellectual, physical, and creative tools required to thrive in a rapidly changing world.
              </p>
            </Card>

            <Card className="p-8 border-t-4 border-[#E6B933] hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#E6B933]/10 rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-[#E6B933]" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To become a leading community resource for youth development, preparing young people not just to participate in society, but to lead within it.
              </p>
            </Card>

            <Card className="p-8 border-t-4 border-[#E6B933] hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#E6B933]/10 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="text-[#E6B933]" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Impact</h3>
              <p className="text-gray-600 leading-relaxed">
                Serving 500+ youth in Northwest Houston and Greenspoint, focusing on At-Risk students through comprehensive life skills, academic, and entrepreneurial development.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Addressing Critical <span className="text-[#E6B933]">Challenges</span>
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full bg-[#E6B933] flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Limited Early Development</h4>
                    <p className="text-gray-600">Many children lack access to stimulating educational environments and structured developmental experiences from an early age.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full bg-[#E6B933] flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Outdated Career Guidance</h4>
                    <p className="text-gray-600">Students receive minimal exposure to modern industries, STEM fields, and high-growth career pathways.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full bg-[#E6B933] flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Lack of Financial Literacy</h4>
                    <p className="text-gray-600">Traditional systems rarely teach business operations, wealth-building strategies, or entrepreneurship fundamentals.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 rounded-full bg-[#E6B933] flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Recruiting Barriers</h4>
                    <p className="text-gray-600">Families struggle to navigate scholarship systems, college recruiting, and community service requirements.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/ru0xvpf8_ME%20TRACK%201.png" 
                alt="Track team" 
                className="rounded-lg shadow-lg w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/sysufm8j_ME%2012u.png" 
                alt="Youth sports" 
                className="rounded-lg shadow-lg w-full h-full object-cover mt-8 hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Impact Areas</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive support across six key areas of youth development
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {impactAreas.map((area, index) => {
              const IconComponent = getIcon(area.icon);
              return (
                <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300 group">
                  <div className="w-14 h-14 bg-[#E6B933]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#E6B933] transition-colors duration-300">
                    <IconComponent className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{area.title}</h3>
                  <p className="text-gray-600">{area.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Community Impact</h2>
            <p className="text-xl text-gray-400">Making a measurable difference in Northwest Houston</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#E6B933] mb-2">75%</div>
              <div className="text-gray-400">At-Risk Youth Supported</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#E6B933] mb-2">$34k-$48k</div>
              <div className="text-gray-400">Median Household Income</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#E6B933] mb-2">500+</div>
              <div className="text-gray-400">Youth Served Annually</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#E6B933] mb-2">3</div>
              <div className="text-gray-400">Core Development Programs</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
